/**
 * Admin frontend conditional-display primitives - Phase 4 of the
 * tenant-agnostic claims migration.
 *
 * Hooks driven by ``GET /api/entities/lookup/?names=…``. A single
 * request fetches presence + attribute names + child-entity metadata
 * for many entity keys at once and caches the result for the session
 * so subsequent ``useEntityRegistered`` / ``useEntityAttribute`` calls
 * are free.
 *
 * The intent: components and routes mount or unmount based on
 * tenant configuration, not on ``if (tenant.slug === 'oc')`` checks.
 * When OC's Lab tab has no Lab entity registered, the tab disappears
 * - and when a new tenant registers their own Lab entity, the tab
 * reappears, with no React changes.
 */

import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'

export interface EntityLookupRecord {
  registered: boolean
  attribute_codes: string[]
  /** Those the tenant has marked mandatory in the Entity Editor. */
  required_attribute_codes?: string[]
  parent_entity_key: string | null
  editor_hidden: boolean
}

export type EntityLookupResponse = Record<string, EntityLookupRecord>

/**
 * Internal - fetch presence/attribute info for a comma-separated list
 * of entity keys. Cached by React Query for the session.
 */
function useEntityLookup(names: string[]) {
  const sortedNames = [...names].sort()
  const key = sortedNames.join(',')
  return useQuery<EntityLookupResponse>({
    queryKey: ['entity-lookup', key],
    queryFn: async () => {
      if (!key) return {}
      const response = await api.get<EntityLookupResponse>('/entities/lookup/', {
        params: { names: key },
      })
      return response.data ?? {}
    },
    enabled: key.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - entity registration is config-time
  })
}

/**
 * True when ``name`` is registered as an EntityType for the current
 * tenant. Loading state resolves as ``false`` until the query lands.
 */
export function useEntityRegistered(name: string): boolean {
  const { data } = useEntityLookup([name])
  return Boolean(data?.[name]?.registered)
}

/**
 * Registration status for ``name`` together with whether the lookup is
 * still resolving. Unlike ``useEntityRegistered`` (which collapses the
 * loading window to ``false``), this exposes ``isResolving`` so callers
 * can show a loading indicator instead of acting on a not-yet-known
 * "unregistered" answer while the ``/entities/lookup/`` request is in
 * flight (SX-229).
 */
export function useEntityRegisteredStatus(name: string): {
  registered: boolean
  isResolving: boolean
} {
  const { data, isLoading } = useEntityLookup([name])
  return {
    registered: Boolean(data?.[name]?.registered),
    isResolving: isLoading,
  }
}

/**
 * True when ``name`` is registered AND has the given attribute
 * ``code_name`` defined.
 */
export function useEntityAttribute(
  name: string,
  attributeCodeName: string,
): boolean {
  const { data } = useEntityLookup([name])
  const record = data?.[name]
  if (!record?.registered) return false
  return record.attribute_codes.includes(attributeCodeName)
}

/**
 * True when the tenant has marked ``attributeCodeName`` required. Off while the
 * lookup resolves, so a surface never demands what was not asked for.
 */
export function useEntityAttributeRequired(
  name: string,
  attributeCodeName: string,
): boolean {
  const { data } = useEntityLookup([name])
  const record = data?.[name]
  if (!record?.registered) return false
  return (record.required_attribute_codes ?? []).includes(attributeCodeName)
}

/**
 * True when ANY of the listed entity names is registered. Useful for
 * mounting a tab group / parent container that's worth showing if at
 * least one of its panels has data.
 */
export function useAnyEntityRegistered(names: string[]): boolean {
  const { data } = useEntityLookup(names)
  if (!data) return false
  return names.some((n) => data[n]?.registered)
}

/**
 * Direct access to the full lookup record - useful when a component
 * needs to inspect e.g. ``parent_entity_key``.
 */
export function useEntityLookupRecord(name: string): EntityLookupRecord | null {
  const { data } = useEntityLookup([name])
  return data?.[name] ?? null
}
